import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, formatDate, formatApiError } from "../lib/apiClient";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";

const STATUS_OPTS = ["all", "in_review", "not_started", "approved", "rejected"];
const NIVEL_OPTS = ["all", "nivel1", "nivel2"];

export default function AdminReviewsPage() {
  const [filters, setFilters] = useState({ status: "all", nivel: "all", reviewer_id: "all", overdue: false });
  const [reviewers, setReviewers] = useState([]);
  const [data, setData] = useState({ total: 0, items: [], pages: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data: r } = await api.get("/admin/reviewers");
        setReviewers(r || []);
      } catch (err) {
        if (process.env.NODE_ENV === "development") {
          console.warn("Reviewers list load failed:", err);
        }
      }
    })();
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ page: "1", limit: "100" });
      if (filters.status !== "all") p.set("status", filters.status);
      if (filters.nivel !== "all") p.set("nivel", filters.nivel);
      if (filters.reviewer_id !== "all") p.set("reviewer_id", filters.reviewer_id);
      if (filters.overdue) p.set("overdue", "true");
      const { data: d } = await api.get(`/admin/reviews?${p}`);
      setData(d);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error cargando revisiones");
    } finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="admin-reviews-page">
      <p className="text-xs uppercase tracking-[0.22em] text-primary font-medium mb-1">Operación</p>
      <h1 className="font-serif text-3xl text-gray-900 mb-1">Revisiones en curso</h1>
      <p className="text-xs text-gray-500 mb-5">{data.total} casos en revisión humana</p>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4 items-end">
        <FilterSelect label="Status" v={filters.status} onChange={(v) => setFilters((f) => ({ ...f, status: v }))} opts={STATUS_OPTS} testid="rev-filter-status" />
        <FilterSelect label="Nivel" v={filters.nivel} onChange={(v) => setFilters((f) => ({ ...f, nivel: v }))} opts={NIVEL_OPTS} testid="rev-filter-nivel" />
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-gray-500 mb-1">Revisor</label>
          <Select value={filters.reviewer_id} onValueChange={(v) => setFilters((f) => ({ ...f, reviewer_id: v }))}>
            <SelectTrigger className="rounded-sm h-9 text-xs" data-testid="rev-filter-reviewer"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="unassigned">Sin asignar</SelectItem>
              {reviewers.map((r) => <SelectItem key={r.id} value={r.id}>{r.email}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <label className="flex items-center gap-2 text-xs text-gray-700 mb-1">
          <input type="checkbox" checked={filters.overdue} onChange={(e) => setFilters((f) => ({ ...f, overdue: e.target.checked }))} data-testid="rev-filter-overdue" />
          Solo overdue (&gt;48h)
        </label>
      </div>

      <div className="border border-gray-200 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-[11px] uppercase tracking-widest text-gray-500 bg-gray-50 border-b">
            <th className="px-3 py-3 text-left">Caso</th>
            <th className="px-3 py-3 text-left">Owner</th>
            <th className="px-3 py-3 text-left">Estado</th>
            <th className="px-3 py-3 text-left">Revisor</th>
            <th className="px-3 py-3 text-right">Días</th>
            <th className="px-3 py-3 text-right">Última actualización</th>
            <th className="px-3 py-3 text-right">Acción</th>
          </tr></thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="px-4 py-10 text-center text-gray-500">Cargando…</td></tr>}
            {!loading && data.items.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-gray-500" data-testid="reviews-empty">Sin revisiones en curso.</td></tr>}
            {!loading && data.items.map((c) => (
              <tr key={c.id} className={`border-b border-gray-100 ${c.is_overdue ? "bg-rose-50/40" : "hover:bg-gray-50"}`} data-testid={`review-row-${c.id}`}>
                <td className="px-3 py-3 max-w-[260px]">
                  <Link to={`/admin/cases/${c.id}`} className="text-gray-900 hover:underline truncate inline-block max-w-full">{c.title}</Link>
                  <div className="text-[11px] text-gray-500 font-mono">{c.id.slice(0, 8)}</div>
                </td>
                <td className="px-3 py-3 text-xs text-gray-700 truncate max-w-[180px]">{c.owner_email || "—"}</td>
                <td className="px-3 py-3 text-xs">{c.state}</td>
                <td className="px-3 py-3 text-xs text-gray-700 truncate max-w-[160px]">{c.reviewer_email || <span className="text-amber-700">Sin asignar</span>}</td>
                <td className="px-3 py-3 text-right">
                  {c.is_overdue && <span className="inline-flex border bg-rose-50 text-rose-900 border-rose-300 px-2 py-[2px] text-[10px] uppercase tracking-wider mr-1" data-testid={`overdue-${c.id}`}>OVERDUE</span>}
                  <span className="text-xs">{c.days_in_review}d</span>
                </td>
                <td className="px-3 py-3 text-xs text-gray-500 text-right whitespace-nowrap">{formatDate(c.updated_at)}</td>
                <td className="px-3 py-3 text-right whitespace-nowrap">
                  <Link to={`/admin/cases/${c.id}`} className="text-xs text-primary underline-offset-2 hover:underline" data-testid={`review-open-${c.id}`}>Abrir →</Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function FilterSelect({ label, v, onChange, opts, testid }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-widest text-gray-500 mb-1">{label}</label>
      <Select value={v} onValueChange={onChange}>
        <SelectTrigger className="rounded-sm h-9 text-xs" data-testid={testid}><SelectValue /></SelectTrigger>
        <SelectContent>{opts.map((o) => <SelectItem key={o} value={o}>{o === "all" ? "Todos" : o}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}
